import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { PenTool, Pencil, Check, X, GripVertical, Pin, PinOff, RotateCcw } from 'lucide-react';

interface DrawingToolbarProps {
  subDrawTool: 'pen' | 'pencil';
  setSubDrawTool: (tool: 'pen' | 'pencil') => void;
  drawStrokeWidth: number;
  setDrawStrokeWidth: (w: number) => void;
  drawStrokeColor: string;
  setDrawStrokeColor: (c: string) => void;
  drawFillColor: string;
  setDrawFillColor: (c: string) => void;
  drawClosedPath: boolean;
  setDrawClosedPath: (b: boolean) => void;
  finishManualDrawing: () => void;
  cancelDrawing: () => void;
  drawingPointsCount: number;
}

export const DrawingToolbar: React.FC<DrawingToolbarProps> = ({
  subDrawTool,
  setSubDrawTool,
  drawStrokeWidth,
  setDrawStrokeWidth,
  drawStrokeColor,
  setDrawStrokeColor,
  drawFillColor,
  setDrawFillColor,
  drawClosedPath,
  setDrawClosedPath,
  finishManualDrawing,
  cancelDrawing,
  drawingPointsCount
}) => {
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const dragRef = useRef<{ startX: number; startY: number; initialX: number; initialY: number } | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  const handlePointerDownDrag = (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!toolbarRef.current) return;

    const rect = toolbarRef.current.getBoundingClientRect();
    const currX = position ? position.x : rect.left;
    const currY = position ? position.y : rect.top;

    if (!position) {
      setPosition({ x: rect.left, y: rect.top });
    }

    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: currX,
      initialY: currY,
    };
    setIsDragging(true);

    const handlePointerMove = (moveEvt: PointerEvent) => {
      if (!dragRef.current) return;
      const dx = moveEvt.clientX - dragRef.current.startX;
      const dy = moveEvt.clientY - dragRef.current.startY;
      setPosition({
        x: Math.max(10, Math.min(window.innerWidth - 300, dragRef.current.initialX + dx)),
        y: Math.max(10, Math.min(window.innerHeight - 80, dragRef.current.initialY + dy)),
      });
    };

    const handlePointerUp = () => {
      setIsDragging(false);
      dragRef.current = null;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  const handleResetPosition = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPosition(null);
    setIsPinned(false);
  };

  const toolbarContent = (
    <div
      ref={toolbarRef}
      style={
        position
          ? {
              position: 'fixed',
              left: `${position.x}px`,
              top: `${position.y}px`,
              transform: 'none',
              zIndex: 99999,
            }
          : {
              position: 'fixed',
              top: '80px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 99999,
            }
      }
      className={`flex items-center gap-2.5 bg-zinc-900/95 backdrop-blur-md border ${
        isDragging
          ? 'border-blue-500 shadow-blue-500/20 ring-2 ring-blue-500/20'
          : isPinned
          ? 'border-amber-500/80 shadow-amber-500/20'
          : 'border-zinc-700/80'
      } shadow-2xl rounded-xl p-2 text-zinc-200 transition-all select-none pointer-events-auto`}
    >
      {/* Drag Handle */}
      <div
        onPointerDown={handlePointerDownDrag}
        className="flex items-center justify-center p-1 cursor-grab active:cursor-grabbing text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 rounded transition-colors"
        title="Click and drag drawing toolbar anywhere"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </div>

      <div className="flex items-center bg-zinc-950 border border-zinc-800 rounded-lg p-0.5 text-xs">
        <button
          onClick={() => setSubDrawTool('pen')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md font-medium transition-colors ${
            subDrawTool === 'pen' ? 'bg-indigo-600 text-white shadow-sm' : 'text-zinc-400 hover:text-zinc-200'
          }`}
          title="Pen Vector Tool: Drop anchor points and drag Bezier handles"
        >
          <PenTool className="w-3.5 h-3.5" />
          <span>Pen Vector</span>
        </button>
        <button
          onClick={() => setSubDrawTool('pencil')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md font-medium transition-colors ${
            subDrawTool === 'pencil' ? 'bg-amber-600 text-white shadow-sm' : 'text-zinc-400 hover:text-zinc-200'
          }`}
          title="Pencil Tool: Draw smooth freehand vector paths"
        >
          <Pencil className="w-3.5 h-3.5" />
          <span>Pencil Brush</span>
        </button>
      </div>

      <div className="h-4 w-[1px] bg-zinc-800" />

      <div className="flex items-center gap-2.5 text-xs">
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-zinc-400 font-mono">Stroke</span>
          <input
            type="number"
            min={1}
            max={30}
            value={drawStrokeWidth}
            onChange={(e) => setDrawStrokeWidth(Number(e.target.value) || 1)}
            className="w-11 px-1 py-0.5 bg-zinc-950 border border-zinc-800 rounded text-xs text-center text-zinc-200 outline-none focus:border-blue-500"
          />
        </div>

        <div className="flex items-center gap-1">
          <input
            type="color"
            value={drawStrokeColor}
            onChange={(e) => setDrawStrokeColor(e.target.value)}
            className="w-5 h-5 rounded cursor-pointer border-0 bg-transparent"
            title="Stroke Color"
          />
        </div>

        <div className="flex items-center gap-1">
          <span className="text-[10px] text-zinc-400 font-mono">Fill</span>
          <input
            type="color"
            value={drawFillColor === 'none' ? '#ffffff' : drawFillColor}
            onChange={(e) => setDrawFillColor(e.target.value)}
            className="w-5 h-5 rounded cursor-pointer border-0 bg-transparent"
            title="Fill Color"
          />
          <button
            onClick={() => setDrawFillColor(drawFillColor === 'none' ? '#2563eb' : 'none')}
            className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${
              drawFillColor === 'none' ? 'bg-zinc-800 text-zinc-400 border-zinc-700' : 'bg-blue-600/20 text-blue-400 border-blue-500/40'
            }`}
          >
            {drawFillColor === 'none' ? 'None' : 'Fill On'}
          </button>
        </div>

        <label className="flex items-center gap-1 text-[11px] text-zinc-300 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={drawClosedPath}
            onChange={(e) => setDrawClosedPath(e.target.checked)}
            className="rounded border-zinc-700 text-blue-600 focus:ring-0"
          />
          Close Path
        </label>
      </div>

      <div className="h-4 w-[1px] bg-zinc-800" />

      <div className="flex items-center gap-1.5">
        <button
          onClick={finishManualDrawing}
          disabled={drawingPointsCount < 2}
          className="px-3 py-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white rounded text-xs font-semibold shadow-sm transition-all flex items-center gap-1"
        >
          <Check className="w-3.5 h-3.5" /> Finish
        </button>

        <button
          onClick={cancelDrawing}
          className="p-1 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded transition-colors"
          title="Cancel Drawing"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Pin / Reset Buttons */}
      <div className="h-4 w-[1px] bg-zinc-800" />
      <div className="flex items-center gap-0.5">
        <button
          onClick={() => setIsPinned(!isPinned)}
          className={`p-1 rounded transition-colors ${
            isPinned
              ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
              : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
          }`}
          title={isPinned ? 'Toolbar is pinned at this position' : 'Pin toolbar at current position'}
        >
          {isPinned ? <Pin className="w-3.5 h-3.5 fill-current" /> : <PinOff className="w-3.5 h-3.5" />}
        </button>

        {position && (
          <button
            onClick={handleResetPosition}
            className="p-1 hover:bg-zinc-800 rounded text-zinc-500 hover:text-zinc-300 transition-colors"
            title="Reset toolbar position to default"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );

  return createPortal(toolbarContent, document.body);
};
